import { toast } from "@/hooks/use-toast";
import { useCallback, useState } from "react";

export interface UseCopyToClipboardOptions {
  showToast?: boolean;
  successMessage?: string;
  errorMessage?: string;
  resetDelay?: number;
}

export function useCopyToClipboard(options: UseCopyToClipboardOptions = {}) {
  const {
    showToast = false,
    successMessage = "Copied to clipboard",
    errorMessage = "Failed to copy",
    resetDelay = 2000,
  } = options;

  const [isCopied, setIsCopied] = useState(false);

  const copyToClipboard = useCallback(
    (text: string) => {
      if (!navigator.clipboard) {
        const error = "Clipboard API not available";
        console.error(error);
        if (showToast) {
          toast({
            variant: "destructive",
            description: errorMessage,
          });
        }
        return;
      }

      navigator.clipboard.writeText(text).then(
        () => {
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), resetDelay);

          if (showToast) {
            toast({
              description: successMessage,
            });
          }
        },
        (err) => {
          console.error("Failed to copy text: ", err);
          if (showToast) {
            toast({
              variant: "destructive",
              description: errorMessage,
            });
          }
        },
      );
    },
    [showToast, successMessage, errorMessage, resetDelay],
  );

  return {
    /**
     * isCopied - resets to false after resetDelay (default 2 seconds)
     */
    isCopied,
    copyToClipboard,
  };
}
